import React, { useCallback, useEffect, useMemo, useState } from 'react';
import uniqBy from 'lodash/uniqBy';
import { useSelector } from 'react-redux';
import { useRouteMatch } from 'react-router';
import { GridReadyEvent, NewColumnsLoadedEvent, RowNode, Column } from 'ag-grid-community';

import { BatchDetailsOperation } from '@common/constants/scenariosConstants';
import { BatchRemoveScenarioDetailsPayload, ScenarioDetailsBatchData } from '@common/schemas/scenarioDetails';
import { useScenarioReportRowDetails } from '@queries/scenarios';
import { useAccountsOptions } from '@queries/accounts';
import { useBatchProcessDetails, useBatchRemoveDetails } from '@mutations/scenarios';

import AgGrid from '../../../shared/agGrid/agGrid.component';
import useGridApi from '../../../shared/hooks/useGridApi.hook';
import { GridMode, rowMetaData, useEditableGridLogic } from '../../../shared/gridEdit/useEditableGridLogic';
import EditableGridButtons from '../../../shared/gridEdit/EditableGridButtons.component';
import { selectedScenarioCurrencySelector, selectedScenarioToCompareSelector } from '../../shared/store/selectors';
import { useCurrentClientId } from '../../shared/utils';
import { useUserNumberFormatterOptions } from '../../../shared/useUserNumberFormatterOptions.hook';
import { useModalControl } from '../../../shared/hooks/useModalControl.hook';

import { CALCULATE_TOTAL_TYPES } from '../totalColumn';
import type {
  BatchRemoveDetailsPayloadWithValue,
  DetailColumnDefinition,
  OnCancelCallback,
  OnRemoveDetailsClick,
  OnSaveCallback,
  ReportDetailsColumnsContext,
  ReportDetailsGridContext,
  ScenarioDetailRendererParams,
} from './types';
import { getColumnDefinitions } from './getColumnDefinitions';

import { useSyncGridState } from './useSyncGridState.hook';
import {
  createDetailRow,
  getDetailKey,
  getScenarioReportRowDetailId,
  useGridApiEventListener,
  useScenariosMetadata,
} from './utils';
import { useFetchDetailsParams } from './useFetchDetailsParams.hook';
import { validateDetailRow } from './validators';
import { DeleteDetailsModalComponent } from './deleteDetailsModal.component';
import { useAdjustGridSizeToRows } from './useAdjustGridSizeToRows.hook';
import { functionalCurrencyOption } from '../../shared/constants';
import { debouncedRebuildReactTooltip } from '../../../shared/debouncedRebuildReactTooltip';
import { ReportColDef } from '../types';


export const ScenarioDetailsRenderer = (params: ScenarioDetailRendererParams) => {
  const {
    api: parentGridApi,
    data: reportRowData,
  } = params;

  const { gridApi, onGridReady: gridApiOnGridReady } = useGridApi();
  const { params: { scenarioId: baseScenarioId } } = useRouteMatch<{ scenarioId: string, }>();
  const clientId = useCurrentClientId();
  const compareScenarioId = useSelector(selectedScenarioToCompareSelector);
  const reportingCurrency = useSelector(selectedScenarioCurrencySelector);
  const isFunctionalCurrencySelected = reportingCurrency === functionalCurrencyOption.value;
  const numberFormatterOptions = useUserNumberFormatterOptions();

  const { scenariosById, isLoading: isLoadingScenariosMetadata } = useScenariosMetadata({
    baseScenarioId: Number(baseScenarioId),
    compareScenarioId,
  });

  const [mode, setMode] = useState(GridMode.View);

  const [{ reportColumnDefs, reportColumns }, setParentColumnsCopy] = useState({
    reportColumnDefs: parentGridApi.getColumnDefs(),
    reportColumns: parentGridApi.getColumns() ?? [],
  });

  useGridApiEventListener(parentGridApi, 'newColumnsLoaded', useCallback(({ api }: NewColumnsLoadedEvent) => {
    setParentColumnsCopy({
      reportColumnDefs: api.getColumnDefs(),
      reportColumns: api.getColumns() ?? [],
    });
  }, []));

  const editableDetailKeys = useMemo(() => reportColumns.filter((column: Column) => column.isCellEditable(params.node))
    .map((column: Column) => {
      const { isBaseScenario, companyCode, countryTaxType } = column.getUserProvidedColDef() as ReportColDef;
      return getDetailKey({
        scenarioId: isBaseScenario ? baseScenarioId : compareScenarioId!,
        companyCode,
        countryTaxType,
      });
    }), [reportColumns, params.node, baseScenarioId, compareScenarioId]);

  const isAnyDetailEditable = !!editableDetailKeys.length;

  const { data: accountsOptions, isFetching: isLoadingAccountsOptions } = useAccountsOptions();

  const {
    params: fetchReportDetailsParams,
    isLoading: areReportDetailsFetchParamsLoading,
  } = useFetchDetailsParams({
    baseScenarioId: Number(baseScenarioId),
    compareScenarioId,
    reportRow: params.data,
    reportingCurrency,
  });

  const { data: details, isFetching: isFetchingDetailsList } = useScenarioReportRowDetails({
    params: fetchReportDetailsParams,
    queryEnabled: !areReportDetailsFetchParamsLoading,
  });

  const removeDetailsModalApi = useModalControl<BatchRemoveDetailsPayloadWithValue[]>();
  const {
    mutate: removeDetails,
    isLoading: isRemovingDetails,
  } = useBatchRemoveDetails({
    onSuccess: removeDetailsModalApi.hideModal,
  });

  const confirmRemoveDetails = (detailsToRemove: BatchRemoveDetailsPayloadWithValue[]) => {
    const remove = removeDetails as ((saveData: BatchRemoveScenarioDetailsPayload[]) => void);
    remove(detailsToRemove.map(({ value, ...detail }) => detail));
  };

  const showDetailsRemoveModal = useCallback<OnRemoveDetailsClick>(({ scenarioDetailsRow }) => {
    if (!gridApi) {
      return;
    }
    const saveData: BatchRemoveDetailsPayloadWithValue[] = [];
    gridApi.getColumns()?.forEach((column: Column) => {
      if (parentGridApi.getColumn(column.getColId())?.isCellEditable(params.node)) {
        const detailKey = getDetailKey(column.getUserProvidedColDef() as DetailColumnDefinition);
        // @ts-ignore
        const detailData = scenarioDetailsRow.details[detailKey];
        if (detailData) {
          saveData.push({
            operation: BatchDetailsOperation.Remove,
            scenarioId: detailData.scenarioId,
            detailId: detailData.detailId,
            companyCode: detailData.companyCode,
            countryTaxType: detailData.countryTaxType,
            fieldName: detailData.fieldName,
            reportName: detailData.reportName,
            value: detailData.value,
          });
        }
      }
    });
    removeDetailsModalApi.showModal(saveData);
  }, [gridApi, params.node, parentGridApi, removeDetailsModalApi]);

  const {
    mutate: saveChanges,
    isLoading: isSavingDetailsChanges,
  } = useBatchProcessDetails({
    // @ts-ignore
    onSuccess: () => setMode(GridMode.View),
  });

  const onSave = useCallback<OnSaveCallback>(({ addedRows, updatedRows, hasErrors } ) => {
    if (hasErrors) {
      return;
    }
    const changedRows = [...addedRows, ...updatedRows];
    const saveData = changedRows.reduce((acc, row) => {
      const originalRow = details.find(({ account, detailName }) =>
        account === row.account && detailName === row.detailName);

      Object.entries(row.details).forEach(([detailKey, detail]) => {
        // @ts-ignore
        if (originalRow?.details?.[detailKey]?.value === detail.value) {
          return;
        }

        acc.push({
          // @ts-ignore
          ...!originalRow?.details[detailKey]
            ? {
              operation: BatchDetailsOperation.Add,
            }
            : {
              operation: BatchDetailsOperation.Update,
              // @ts-ignore
              detailId: originalRow.details[detailKey].detailId,
            },
          account: row.account,
          detailName: row.detailName,
          scenarioId: detail.scenarioId,
          companyCode: detail.companyCode,
          countryTaxType: detail.countryTaxType,
          reportName: detail.reportName,
          fieldName: detail.fieldName,
          value: detail.value,
        } as ScenarioDetailsBatchData);
      });
      return acc;
    }, [] as ScenarioDetailsBatchData[]);

    saveChanges(
      reportRowData.calculateTotal === CALCULATE_TOTAL_TYPES.SUM_PER_COMPANY_CODE
        ? uniqBy(saveData, ({ scenarioId, companyCode, account, detailName }) => `${scenarioId}-${companyCode}-${account}-${detailName}`)
        : saveData
    );
  }, [details, reportRowData.calculateTotal, saveChanges]);


  const onCancel = useCallback<OnCancelCallback>((hasChanged, reset) => {
    setMode(GridMode.View);
    reset();
  }, []);

  const gridContext = useMemo((): ReportDetailsGridContext => ({
    editableDetailKeys,
  }), [editableDetailKeys]);

  const {
    addRow,
    cancel: onCancelClick,
    save: onSaveClick,
    setRowData,
    onGridReady: editableGridLogicOnGridReady,
    editedRowId,
    setEditedRow,
    ...gridParams
  } = useEditableGridLogic({
    data: details,
    onCancel,
    mode,
    createRow: createDetailRow,
    validateRow: validateDetailRow,
    clientId,
    onSave,
    getRowUniqueId: getScenarioReportRowDetailId,
    includeMetaWhenSaving: true,
    context: gridContext,
  });

  const refreshCells = useCallback((rowNodes?: RowNode[]) => {
    setTimeout(() => {
      gridApi?.refreshCells({
        force: true,
        rowNodes,
      });
    }, 0);
  }, [gridApi]);

  const onGridReady = useCallback((event: GridReadyEvent) => {
    gridApiOnGridReady(event);
    editableGridLogicOnGridReady(event);
  }, [editableGridLogicOnGridReady, gridApiOnGridReady]);

  const onAddClick = useCallback(() => {
    setMode(GridMode.Add);
    addRow();
  }, [addRow]);

  const onEditAllClick = useCallback(() => {
    setMode(GridMode.EditAll);
  }, []);

  const editRow = useCallback((data, node) => {
    setMode(GridMode.Edit);
    setEditedRow(data);
    refreshCells([node]);
  }, [setEditedRow, refreshCells]);

  useEffect(() => {
    refreshCells();
    debouncedRebuildReactTooltip();
  }, [isFunctionalCurrencySelected, refreshCells, isAnyDetailEditable]);

  const columnsContext = useMemo((): ReportDetailsColumnsContext => ({
    mode,
    editedRowId,
    accountsOptions,
    isFunctionalCurrencySelected,
    isAnyDetailEditable,
  }), [mode, editedRowId, accountsOptions, isFunctionalCurrencySelected, isAnyDetailEditable]);

  const columnDefinitions = useMemo(() => getColumnDefinitions({
    baseScenarioId: Number(baseScenarioId),
    compareScenarioId,
    reportColumns: reportColumnDefs,
    parentGridApi,
    reportGridRow: params,
    setRowData,
    editRow,
    context: columnsContext,
    numberFormatterOptions,
    showDetailsRemoveModal,
  }), [
    baseScenarioId, compareScenarioId, showDetailsRemoveModal,
    reportColumnDefs, parentGridApi, params, setRowData, editRow, columnsContext, numberFormatterOptions,
  ]);

  useEffect(() => {
    refreshCells();
  }, [columnDefinitions, refreshCells]);

  const isGridLoading = isFetchingDetailsList
    || isLoadingAccountsOptions
    || areReportDetailsFetchParamsLoading
    || isRemovingDetails
    || isSavingDetailsChanges;

  const { adjustColumnsToParent } = useSyncGridState({
    gridApi,
    parentGridApi,
  });

  useEffect(adjustColumnsToParent, [adjustColumnsToParent, details, reportRowData]);

  const { containerRef } = useAdjustGridSizeToRows({
    parentGridApi,
    parentNode: params.node,
    details,
  });

  return (
    <div className="expandable-details-list">
      <div className="ag-subgrid ag-theme-bootstrap with-editable-grid" ref={containerRef}>
        <EditableGridButtons
          extraButtonOnLeft={
            <h6>{reportRowData.label} details</h6>
          }
          onAddClick={onAddClick}
          disabled={{
            add: !isFunctionalCurrencySelected || !isAnyDetailEditable,
            editAll: !isFunctionalCurrencySelected || !isAnyDetailEditable,
          }}
          addButtonText="Add detail"
          mode={mode}
          onCancelClick={onCancelClick}
          onSaveClick={onSaveClick}
          onEditAllClick={onEditAllClick}
          /* If now whole master grid row (containing details grid) is fully visible in a viewport,
           * AgGrid grid is trying to fit the row in the viewport by scrolling grid's body.
           * As result, the click event is not fired (because releasing button happens over different element than pressing it)
           * As workaround, we need to react on mousedown event instead.
           */
          mouseDownInsteadOfClick
        />
        <AgGrid
          columnDefs={columnDefinitions}
          isGridLoading={isGridLoading}
          suppressSizeColumnsToFit
          onGridReady={onGridReady}
          suppressHorizontalScroll
          suppressRowClickSelection
          suppressScrollOnNewData
          enableBrowserTooltips
          stopEditingWhenCellsLoseFocus
          suppressCellFocus
          suppressColumnMoveAnimation
          pinnedBottomRowData={[{ meta: rowMetaData }]}
          {...gridParams}
          context={columnsContext}
        />
        <DeleteDetailsModalComponent
          scenarios={scenariosById}
          detailsToRemove={removeDetailsModalApi.modalData}
          isVisible={removeDetailsModalApi.isModalVisible}
          isLoadingScenarios={isLoadingScenariosMetadata}
          isRemoving={isRemovingDetails}
          removeDetails={confirmRemoveDetails} // eslint-disable-line react/jsx-no-bind
          hideModal={removeDetailsModalApi.hideModal}
        />
      </div>
    </div>
  );
};
